import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, } from "react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Textarea, } from "../../../components/base";
import { useUiTranslation } from "../../../i18n";
const MIN_REGION_SIZE = 0.01;
const KEYBOARD_STEP = 0.01;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const roundCoordinate = (value) => Number(value.toFixed(4));
const roundRegion = (region) => {
    const x = roundCoordinate(region.x);
    const y = roundCoordinate(region.y);
    return {
        x,
        y,
        width: Math.min(roundCoordinate(region.width), roundCoordinate(1 - x)),
        height: Math.min(roundCoordinate(region.height), roundCoordinate(1 - y)),
    };
};
const toPercentage = (value) => Math.round(value * 1000) / 10;
const regionFromPoints = (start, end) => roundRegion({
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
});
export const ImageEditDialog = ({ open, target, submitting = false, onOpenChange, onSubmit, }) => {
    const { t } = useUiTranslation();
    const [prompt, setPrompt] = useState("");
    const [region, setRegion] = useState(null);
    const dragStartRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        setPrompt("");
        setRegion(null);
        dragStartRef.current = null;
    }, [open, target?.attachmentId]);
    const pointFromPointer = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        return {
            x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
            y: clamp((event.clientY - rect.top) / rect.height, 0, 1),
        };
    };
    const handlePointerDown = (event) => {
        if (submitting || event.button !== 0)
            return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = pointFromPointer(event);
        dragStartRef.current = point;
        setRegion({ x: point.x, y: point.y, width: 0, height: 0 });
    };
    const handlePointerMove = (event) => {
        if (!dragStartRef.current || submitting)
            return;
        setRegion(regionFromPoints(dragStartRef.current, pointFromPointer(event)));
    };
    const handlePointerUp = (event) => {
        if (!dragStartRef.current)
            return;
        const nextRegion = regionFromPoints(dragStartRef.current, pointFromPointer(event));
        dragStartRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setRegion(nextRegion.width >= MIN_REGION_SIZE &&
            nextRegion.height >= MIN_REGION_SIZE
            ? nextRegion
            : null);
    };
    const handleSelectionKeyDown = (event) => {
        if (submitting)
            return;
        if (event.key === "Enter" && !region) {
            event.preventDefault();
            setRegion({ x: 0.25, y: 0.25, width: 0.5, height: 0.5 });
            return;
        }
        if ((event.key === "Delete" || event.key === "Backspace") && region) {
            event.preventDefault();
            setRegion(null);
            return;
        }
        if (!region || !event.key.startsWith("Arrow"))
            return;
        event.preventDefault();
        const horizontal = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
        const vertical = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
        if (event.shiftKey) {
            setRegion(roundRegion({
                ...region,
                width: clamp(region.width + horizontal * KEYBOARD_STEP, MIN_REGION_SIZE, 1 - region.x),
                height: clamp(region.height + vertical * KEYBOARD_STEP, MIN_REGION_SIZE, 1 - region.y),
            }));
            return;
        }
        setRegion(roundRegion({
            ...region,
            x: clamp(region.x + horizontal * KEYBOARD_STEP, 0, 1 - region.width),
            y: clamp(region.y + vertical * KEYBOARD_STEP, 0, 1 - region.height),
        }));
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        const normalizedPrompt = prompt.trim();
        if (!normalizedPrompt || !region || submitting)
            return;
        await onSubmit(normalizedPrompt, region);
    };
    const validRegion = region &&
        region.width >= MIN_REGION_SIZE &&
        region.height >= MIN_REGION_SIZE
        ? region
        : null;
    const selectionStatus = region
        ? t("chat.imageEdit.selectionStatus", {
            x: toPercentage(region.x),
            y: toPercentage(region.y),
            width: toPercentage(region.width),
            height: toPercentage(region.height),
        })
        : t("chat.imageEdit.selectionStatusNone");
    return (_jsx(Dialog, { open: open, onOpenChange: (nextOpen) => {
            if (!submitting)
                onOpenChange(nextOpen);
        }, children: _jsxs(DialogContent, { className: "max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-5xl", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: t("chat.imageEdit.title") }), _jsx(DialogDescription, { children: t("chat.imageEdit.description") })] }), _jsxs("form", { className: "grid gap-4", onSubmit: handleSubmit, children: [target && (_jsx("div", { className: "flex justify-center rounded-lg bg-muted/50 p-2", children: _jsxs("div", { className: "relative inline-block max-w-full overflow-hidden rounded-md", children: [_jsx("img", { src: target.src, alt: target.alt, className: "block max-h-[62vh] max-w-full select-none object-contain", draggable: false }), _jsx("div", { role: "application", tabIndex: 0, "aria-label": t("chat.imageEdit.canvasLabel"), "aria-describedby": "image-edit-instructions image-edit-selection-status", className: "absolute inset-0 cursor-crosshair touch-none outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset", onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onPointerCancel: () => {
                                            dragStartRef.current = null;
                                            setRegion(null);
                                        }, onKeyDown: handleSelectionKeyDown, children: region && (_jsx("div", { "data-testid": "image-edit-region", className: "pointer-events-none absolute border-2 border-white shadow-[0_0_0_9999px_rgb(0_0_0/0.45)]", style: {
                                                left: `${region.x * 100}%`,
                                                top: `${region.y * 100}%`,
                                                width: `${region.width * 100}%`,
                                                height: `${region.height * 100}%`,
                                            } })) })] }) })), _jsx("p", { id: "image-edit-instructions", className: "text-sm text-muted-foreground", children: t("chat.imageEdit.instructions") }), _jsx("p", { id: "image-edit-selection-status", role: "status", "aria-live": "polite", "aria-atomic": "true", className: "sr-only", children: selectionStatus }), _jsxs("label", { className: "grid gap-2 text-sm font-medium", children: [t("chat.imageEdit.promptLabel"), _jsx(Textarea, { value: prompt, onChange: (event) => setPrompt(event.target.value), placeholder: t("chat.imageEdit.promptPlaceholder"), disabled: submitting, required: true })] }), _jsxs(DialogFooter, { children: [_jsx(Button, { type: "button", variant: "outline", disabled: !region || submitting, onClick: () => setRegion(null), children: t("chat.imageEdit.clear") }), _jsx(Button, { type: "submit", disabled: !validRegion || !prompt.trim() || submitting, children: submitting
                                        ? t("chat.imageEdit.submitting")
                                        : t("chat.imageEdit.submit") })] })] })] }) }));
};
//# sourceMappingURL=ImageEditDialog.js.map